import { useState, useCallback, useEffect, useRef } from 'react';
import { useFileUpload } from '@hooks/useFileUpload';
import { RcFile } from 'antd/es/upload';
import { useUploadProgress } from '@hooks/useUploadProgress';
import { useScanStatusPoll } from '@hooks/useScanStatusPoll';
import { useLazyQuery } from '@apollo/client';
import { App } from 'antd';
import { CHECK_MONTH_APPROVAL_STATUS } from 'graphql/prodStatDashboard.gql';
import { CHECK_PREVIOUS_MONTH_INVOICES_PENDING } from 'graphql/invoices.gql';
import { useApi } from '@hooks/useApi';

// =========================
// Filename Validation
// =========================
const USE_EXPRESS_PIPELINE = process.env.REACT_APP_USE_EXPRESS_PIPELINE === 'true';

const validateFileName = (fileName: string): { valid: boolean; error?: string } => {
  const pattern = /^([A-Z0-9_-]+)\s+(\d{4})-(\d{2})\.csv$/i;
  const match = pattern.exec(fileName);

  if (!match) {
    return {
      valid: false,
      error: 'Invalid filename format. Expected: metertype YYYY-MM.csv (e.g., AMI 2025-05.csv, Manual 2024-12.csv)',
    };
  }

  const year = Number(match[2]);
  const month = Number(match[3]);

  if (year < 2000 || year > 2099) {
    return {
      valid: false,
      error: `Invalid year "${year}" in filename. Must be 2000–2099.`,
    };
  }

  if (month < 1 || month > 12) {
    return {
      valid: false,
      error: `Invalid month "${month}" in filename. Must be 01–12.`,
    };
  }

  return { valid: true };
};
const getStatusStreamUrl = (runId) => {
  if (runId && runId.startsWith('express_')) {
    return `/storage/meter-data/express/status-stream/${runId}`;
  }
  return `/storage/meter-data/pipeline-status-stream/${runId}`;
};
// =========================
// Extract month-year from filename
// =========================
const extractMonthYearFromFileName = (fileName: string): string | null => {
  const pattern = /^[A-Z0-9_-]+\s+(\d{4})-(\d{2})\.csv$/i;
  const match = pattern.exec(fileName);
  if (!match) return null;
  return `${match[1]}-${match[2]}`;
};

// =========================
// Format month-year for display (e.g. "2025-05" → "May 2025")
// =========================
const formatMonthYearForDisplay = (monthYear: string): string => {
  const [year, month] = monthYear.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
};

// =========================
// Hook
// =========================

type ValidationFeedback = {
  type: 'error' | 'success';
  heading: string;
  message?: string;
};

const UPLOAD_LOCK_POLL_INTERVAL_MS = 5_000;

const useFileIngestionUploadSection = () => {
  const { modal } = App.useApp();
  const { uploadFiles, uploading } = useFileUpload();
  const { checkUploadLock, isReady } = useApi();
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [uploadingAll, setUploadingAll] = useState(false);
  const [uploadLocked, setUploadLocked] = useState(false);
  const lockPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [pipelineRunId, setPipelineRunId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem('pipeline_progress');
      if (stored) {
        const progress = JSON.parse(stored);
        if (['connected', 'warming_up', 'processing'].includes(progress.status)) {
          return progress.runId;
        }
      }
    } catch { }
    return null;
  });

  const [validationFeedback, setValidationFeedback] =
    useState<ValidationFeedback | null>(null);

  // Meter uploads are held for malware scanning before the ADF pipeline is triggered.
  // scanBatchId tracks the batch we're waiting on; once it resolves Clean we hand off
  // to pipelineRunId/useUploadProgress exactly as before.
  const [scanBatchId, setScanBatchId] = useState<string | null>(null);
  const { scanProgress, resetScan } = useScanStatusPoll(scanBatchId);

  const { progress: pipelineProgress, clearProgress } = useUploadProgress(pipelineRunId);

  // Bridges the gap between "scan resolved Clean" and "SSE has delivered its first
  // status update" — pipelineRunId is set synchronously, but useUploadProgress's SSE
  // connection only updates pipelineProgress once the 'connected' message actually
  // arrives, which is a real network round-trip. Without this, the progress bar would
  // briefly disappear (pipelineProgress still 'idle') between those two events.
  const isStartingPipeline = !!pipelineRunId && pipelineProgress.status === 'idle';

  const uploadProgress =
    scanProgress.status === 'scanning'
      ? { status: 'scanning' as const, message: 'Scanning uploaded files for malware...', progress: 0 }
      : isStartingPipeline
      ? { status: 'starting' as const, message: 'Starting processing...', progress: 0 }
      : pipelineProgress;

  const [checkMonthApprovalStatus] = useLazyQuery(CHECK_MONTH_APPROVAL_STATUS, {
    fetchPolicy: 'network-only',
  });

  const [checkPreviousMonthInvoicesPending] = useLazyQuery(CHECK_PREVIOUS_MONTH_INVOICES_PENDING, {
    fetchPolicy: 'network-only',
  });

  // Shows a blocking confirm dialog if any of the given months' previous month
  // still has unapproved invoices. Resolves true if the user should proceed
  // (either nothing pending, or they chose to proceed anyway), false to abort.
  const confirmPreviousMonthInvoicesApproved = useCallback(
    async (monthYearsToCheck: string[]): Promise<boolean> => {
      let maxPendingCount = 0;
      let maxPendingMonthYear: string | null = null;

      for (const monthYear of monthYearsToCheck) {
        const { data } = await checkPreviousMonthInvoicesPending({ variables: { monthYear } });
        const pendingCount = data?.checkPreviousMonthInvoicesPending?.pendingCount ?? 0;
        if (pendingCount > maxPendingCount) {
          maxPendingCount = pendingCount;
          maxPendingMonthYear = data.checkPreviousMonthInvoicesPending.monthYear;
        }
      }

      if (maxPendingCount === 0) return true;

      const displayMonth = maxPendingMonthYear ? formatMonthYearForDisplay(maxPendingMonthYear) : 'the previous month';

      return new Promise<boolean>((resolve) => {
        modal.confirm({
          title: 'Previous Month Invoices Pending Approval',
          content: `There ${maxPendingCount === 1 ? 'is' : 'are'} ${maxPendingCount} invoice${maxPendingCount === 1 ? '' : 's'} from ${displayMonth} that ${maxPendingCount === 1 ? 'has' : 'have'} not been approved yet. Please verify these invoices before proceeding, as they may affect the calculations for the current month.`,
          okText: 'Proceed',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
    },
    [checkPreviousMonthInvoicesPending, modal]
  );

  const isProcessing =
    scanProgress.status === 'scanning' ||
    uploadProgress.status === 'starting' ||
    uploadProgress.status === 'connected' ||
    uploadProgress.status === 'warming_up' ||
    uploadProgress.status === 'processing';

  // =========================
  // React to malware scan resolution: hand off to the pipeline SSE flow when
  // Clean, or surface a blocking error when Malicious/failed to start.
  // =========================
  useEffect(() => {
    if (scanProgress.status === 'clean' && scanProgress.runId) {
      setPipelineRunId(scanProgress.runId);
      setScanBatchId(null);
      setValidationFeedback({
        type: 'success',
        heading: 'Scan Successful',
        message: 'File processing started.',
      });
      resetScan();
    } else if (scanProgress.status === 'malicious') {
      setValidationFeedback({
        type: 'error',
        heading: 'Malware Detected',
        message: `The following file(s) were flagged as malicious and will not be processed: ${scanProgress.maliciousFiles.join(', ')}. Please contact your administrator.`,
      });
      setScanBatchId(null);
      resetScan();
    } else if (scanProgress.status === 'error') {
      setValidationFeedback({
        type: 'error',
        heading: 'Processing Failed to Start',
        message: scanProgress.errorMessage || 'Files passed the malware scan, but processing could not be started. Please contact your administrator.',
      });
      setScanBatchId(null);
      resetScan();
    }
  }, [scanProgress, resetScan]);

  // =========================
  // Upload lock polling — disables upload for all users when any pipeline/dataflow is running
  // =========================
  useEffect(() => {
    // Wait for the access token to actually be available — polling before then sent
    // "Authorization: Bearer undefined", which the (now-enforced) auth check on
    // /storage correctly rejects as a 401 on the very first poll.
    if (!isReady) return undefined;

    const poll = async () => {
      const { locked, source } = await checkUploadLock();
      setUploadLocked(locked && source !== 'dataflow');
    };

    poll();
    lockPollRef.current = setInterval(poll, UPLOAD_LOCK_POLL_INTERVAL_MS);

    return () => {
      if (lockPollRef.current !== null) clearInterval(lockPollRef.current);
    };
  }, [checkUploadLock, isReady]);

  // =========================
  // File Select
  // =========================
  const handleSelectFile = useCallback(
    (file: RcFile): void => {
      if (isProcessing) {
        setValidationFeedback({
          type: 'error',
          heading: 'Upload Blocked',
          message: 'Please wait until current processing finishes.',
        });
        return;
      }

      const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
      if (!isCSV) {
        setValidationFeedback({
          type: 'error',
          heading: 'Invalid File',
          message: `"${file.name}" is not a CSV file.`,
        });
        return;
      }

      const validation = validateFileName(file.name);
      if (!validation.valid) {
        setValidationFeedback({
          type: 'error',
          heading: 'Invalid Filename',
          message: validation.error,
        });
        return;
      }

      setFileList((prev) => [...prev, file]);
      setValidationFeedback(null);
    },
    [isProcessing]
  );

  // =========================
  // File Remove
  // =========================
  const handleRemoveFile = useCallback((file: RcFile) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  }, []);

  // =========================
  // Upload All
  // =========================
  const handleUploadAll = useCallback(async () => {
    setUploadingAll(true);
    setValidationFeedback(null);

    try {
      // --- Approval gate: check each unique month-year in the selected files ---
      const monthYearsToCheck = Array.from(
        new Set(
          fileList
            .map((f) => extractMonthYearFromFileName(f.name))
            .filter((m): m is string => m !== null)
        )
      );

      for (const monthYear of monthYearsToCheck) {
        const { data: approvalData } = await checkMonthApprovalStatus({
          variables: { monthYear },
        });

        if (approvalData?.checkMonthApprovalStatus?.hasApprovedRecords) {
          const displayMonth = formatMonthYearForDisplay(monthYear);
          setValidationFeedback({
            type: 'error',
            heading: 'Upload Blocked — Month Already Approved',
            message: `The Prod Stats for ${displayMonth} have already been approved. Please check that you have the correct file, and that the file name is accurate.`,
          });
          return;
        }
      }

      // --- Warn if previous month's invoices are still pending approval ---
      const shouldProceed = await confirmPreviousMonthInvoicesApproved(monthYearsToCheck);
      if (!shouldProceed) {
        return;
      }

      // --- Proceed with upload ---
      const response = await uploadFiles(fileList);

      if (!response || response.success === false) {
        const rawMessage = response?.message || 'Upload failed.';
        const [heading, ...rest] = rawMessage.split('\n');
        setValidationFeedback({
          type: 'error',
          heading: heading || 'Upload Failed',
          message: rest.join('\n').trim(),
        });
        return;
      }

      // Meter uploads no longer start the pipeline immediately — they wait on a
      // malware scan (tracked by batchId). Legacy/express responses that still
      // include a runId directly skip straight to the pipeline SSE flow.
      const runId = response.data?.pipeline?.runId;
      const batchId = response.data?.batchId;

      if (runId) {
        setPipelineRunId(runId);
      } else if (batchId) {
        setScanBatchId(batchId);
      }

      setFileList([]);
      setValidationFeedback({
        type: 'success',
        heading: 'Upload Successful',
        message: batchId && !runId
          ? 'Files uploaded. Scanning for malware before processing begins.'
          : 'Processing has started.',
      });
    } catch (err: any) {
      setValidationFeedback({
        type: 'error',
        heading: 'Unexpected Error',
        message: err?.message || 'Upload failed.',
      });
    } finally {
      setUploadingAll(false);
    }
  }, [fileList, uploadFiles, checkMonthApprovalStatus, confirmPreviousMonthInvoicesApproved]);

  // =========================
  // Pipeline Status Feedback
  // =========================
  useEffect(() => {
    if (uploadProgress.status === 'completed') {
      setValidationFeedback({
        type: 'success',
        heading: 'Processing Completed',
      });
    } else if (uploadProgress.status === 'failed') {
      setValidationFeedback({
        type: 'error',
        heading: 'Processing Failed',
        message: uploadProgress.message,
      });
    } else if (uploadProgress.status === 'cancelled') {
      setValidationFeedback({
        type: 'error',
        heading: 'Pipeline Cancelled',
        message: 'You may upload new files now.',
      });
    }
  }, [uploadProgress.status, uploadProgress.message]);

  const handleCloseProgress = useCallback(() => {
    clearProgress();
    setPipelineRunId(null);
  }, [clearProgress]);

  return {
    fileList,
    uploading,
    uploadingAll,
    uploadLocked,
    validationFeedback,
    uploadProgress,
    isProcessing,
    handleSelectFile,
    handleRemoveFile,
    handleUploadAll,
    handleCloseProgress,
    USE_EXPRESS_PIPELINE,
    getStatusStreamUrl,
  };
};

export default useFileIngestionUploadSection;